import { useRef, useState, type CSSProperties } from 'react';
import { Photo } from '../components/Photo';
import { useSectionMetrics } from '../motion/scroll';
import { range } from '../motion/tokens';
import { useLocale } from '../i18n/locale';
import { sectionId } from '../content/site';
import { HOME, SOURCING_PHOTO } from './content';
import { Lines } from './Lines';

/**
 * Control at origin — whatever the origin (China, the United States or another market): the supplier
 * file completes check by check as the section crosses the screen, then the four conditions that must
 * be met before cargo moves light up. Illustrative states, not regulatory approvals.
 */
export function Sourcing() {
  const { locale } = useLocale(); const copy = HOME[locale].sourcing;
  const ref = useRef<HTMLElement>(null);
  const [checked, setChecked] = useState(0);
  const [gates, setGates] = useState(0);
  useSectionMetrics(ref, ({ pass, visible }) => {
    if (!visible) return;
    const c = Math.round(range(pass, 0.16, 0.38) * copy.checks.length); const g = Math.round(range(pass, 0.42, 0.6) * copy.gates.length);
    setChecked(previous => (previous === c ? previous : c)); setGates(previous => (previous === g ? previous : g));
  });
  return (
    <section ref={ref} id={sectionId('sourcing', locale)} className="china" data-tone="dark" aria-labelledby="sourcing-title">
      <div className="china__inner wrap">
        <header className="section-head">
          <div className="section-head__title">
            <p className="section-eyebrow">{copy.eyebrow}</p>
            <h2 id="sourcing-title" className="section-title"><Lines lines={copy.title} /></h2>
          </div>
          <p className="section-body"><Lines lines={copy.body} /></p>
        </header>
        <div className="china__evidence">
          <Photo id={SOURCING_PHOTO} alt={copy.photoAlt} sizes="(min-width: 1024px) 58vw, 100vw" className="china__photo" />
          <div className="china__dossier" role="group" aria-label={copy.dossier}>
            <p className="china__dossier-title">{copy.dossier}<span className="china__dossier-count">{checked} {copy.of} {copy.checks.length}</span></p>
            <ul role="list">
              {copy.checks.map((check, index) => (
                <li key={check.title} className={index < checked ? 'is-checked' : undefined}>
                  <span className="china__check" aria-hidden="true" />
                  <span className="china__check-title">{check.title}</span>
                  <span className="china__check-body">{check.body}</span>
                  <span className="visually-hidden">{index < checked ? copy.verified : copy.pending}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="china__gates">
          <div className="china__gates-copy">
            <h3 className="china__gates-title">{copy.gatesTitle}</h3>
            <p className="section-body">{copy.gatesBody}</p>
          </div>
          <ol className="china__gate-row" style={{ '--lit': gates } as CSSProperties}>
            {copy.gates.map((gate, index) => (
              <li key={gate} className={index < gates ? 'is-on' : undefined}><span className="china__gate-dot" aria-hidden="true" />{gate}</li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
